import { Typography } from '@mui/material';
import { PieChart as MuiPieChart } from '@mui/x-charts/PieChart';
import React, { useEffect, useRef, useState } from 'react';

import { formatMoney } from '../../functions/utils';
import { hexToRGBA } from '../BarChart';
import EnhacedBox from '../Box';

interface PieChartProps {
  data: {
    id: string;
    label: string;
    value: number;
  }[];
  title?: string;
  icon?: React.ReactNode;
  color: string;
  formatAsMoney?: boolean;
}

const PieChart = ({
  data,
  title,
  icon,
  color,
  formatAsMoney = false,
}: PieChartProps) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);

  const formatValue = (value: number) => {
    if (formatAsMoney) {
      return formatMoney(value);
    }
    return value.toLocaleString('es-CL');
  };

  useEffect(() => {
    const updateDimensions = () => {
      if (elementRef.current) {
        const { width } = elementRef.current.getBoundingClientRect();
        setHeight(width * 0.5);
        setWidth(width - 40);
      }
    };

    updateDimensions();

    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  return (
    <EnhacedBox small ref={elementRef}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: hexToRGBA(color, 0.12),
          }}
        >
          {icon}
        </div>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1.1rem',
          }}
        >
          {title}
        </Typography>
      </div>
      <MuiPieChart
        series={[
          {
            data,
            innerRadius: 30,
            outerRadius: 100,
            paddingAngle: 5,
            cornerRadius: 5,
            highlightScope: { fade: 'global', highlight: 'item' },
            faded: {
              innerRadius: 30,
              additionalRadius: -30,
              color: 'gray',
            },
            valueFormatter: (object) => formatValue(object.value),
          },
        ]}
        height={height}
        width={width}
        slotProps={{
          legend: {
            direction: 'column',
            position: { vertical: 'middle', horizontal: 'right' },
            itemMarkWidth: 12,
            itemMarkHeight: 12,
            labelStyle: { fontSize: '14px' },
          },
        }}
      />
    </EnhacedBox>
  );
};

export default PieChart;
